import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Users, Wallet, Calendar, Bot, Moon, Trophy, Gamepad2, MessageSquare,
  TrendingUp, Loader2, RefreshCw, Clock, AlertTriangle, Gift, Receipt,
  ClipboardEdit, Sparkle, BellRing, ArrowRight, ShieldAlert, Activity,
  BarChart3, Star, Megaphone, Settings, MessageCircle, IdCard, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { ReflectedShareModal } from "@/components/admin/ReflectedShareModal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n, cur = "") => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}${cur ? ` ${cur}` : ""}`;
const fmtDT = (iso, opts = { dateStyle: "short", timeStyle: "short" }) => {
  try { return new Date(iso).toLocaleString("es-MX", opts); } catch { return iso || "—"; }
};
const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "hace un momento";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
};
const openChat = (user_id, user_name) => window.dispatchEvent(new CustomEvent("admin:openChat", { detail: { user_id, user_name } }));
const openDossier = (user_id) => window.dispatchEvent(new CustomEvent("admin:openDossier", { detail: { user_id } }));

/* ---------- small building blocks ---------- */
const Kpi = ({ icon: Icon, label, value, sub, tone = "gold", testid, onClick }) => {
  const tones = {
    gold: "border-[#d4af37]/30 text-[#d4af37]",
    emerald: "border-emerald-500/30 text-emerald-300",
    sky: "border-sky-500/30 text-sky-300",
    amber: "border-amber-500/30 text-amber-300",
    purple: "border-purple-500/30 text-purple-300",
    rose: "border-rose-500/30 text-rose-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left glass-dark rounded-xl p-3.5 border ${tones[tone]} hover:bg-white/[0.03] transition-colors ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-400"><Icon className="w-3 h-3" /> {label}</div>
      <div className={`text-xl sm:text-2xl font-bold leading-tight mt-1 ${tones[tone].split(" ")[1]}`}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5 truncate">{sub}</div>}
    </button>
  );
};

const AlertChip = ({ icon: Icon, label, count, tone, onClick, testid }) => {
  const active = count > 0;
  const tones = {
    rose: "border-rose-500/60 bg-rose-500/15 text-rose-200",
    amber: "border-amber-500/60 bg-amber-500/15 text-amber-200",
    sky: "border-sky-500/60 bg-sky-500/15 text-sky-200",
    purple: "border-purple-500/60 bg-purple-500/15 text-purple-200",
    emerald: "border-emerald-500/60 bg-emerald-500/15 text-emerald-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${active ? tones[tone] + " hover:brightness-110" : "border-white/10 bg-black/30 text-neutral-500"}`}
    >
      <Icon className={`w-3.5 h-3.5 ${active ? "" : "opacity-50"}`} />
      {label}
      <span className={`ml-0.5 min-w-[20px] text-center rounded-full px-1.5 py-0.5 text-[10px] ${active ? "bg-black/40" : "bg-white/5"}`}>{count}</span>
    </button>
  );
};

const Panel = ({ title, icon: Icon, children, action, testid, className = "" }) => (
  <div className={`glass-dark rounded-2xl p-4 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="font-serif text-sm text-white flex items-center gap-2"><Icon className="w-4 h-4 text-[#d4af37]" /> {title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const RevenueChart = ({ series }) => {
  const currencies = Object.keys(series || {});
  const [cur, setCur] = useState(currencies[0] || "MXN");
  useEffect(() => { if (!currencies.includes(cur) && currencies[0]) setCur(currencies[0]); /* eslint-disable-next-line */ }, [currencies.join(",")]);
  const data = series?.[cur] || [];
  const max = Math.max(1, ...data.map((d) => d.amount));
  const total = data.reduce((s, d) => s + d.amount, 0);
  const W = 600, H = 120;
  const bw = W / Math.max(1, data.length);
  return (
    <div data-testid="overview-revenue-chart">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] text-neutral-400">Total 30 días: <b className="text-emerald-300">{fmtMoney(total, cur)}</b></div>
        {currencies.length > 1 && (
          <div className="flex gap-1">
            {currencies.map((c) => (
              <button key={c} type="button" onClick={() => setCur(c)} data-testid={`overview-chart-cur-${c}`} className={`text-[10px] px-2 py-0.5 rounded-full border ${cur === c ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10" : "border-white/10 text-neutral-500"}`}>{c}</button>
            ))}
          </div>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ovGold" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f3d878" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0.55" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => <line key={g} x1="0" x2={W} y1={H * g} y2={H * g} stroke="rgba(255,255,255,0.05)" />)}
        {data.map((d, i) => {
          const h = Math.max(1.5, (d.amount / max) * (H - 6));
          return (
            <g key={d.date}>
              <rect x={i * bw + 1.5} y={H - h} width={Math.max(2, bw - 3)} height={h} rx="2" fill={d.amount > 0 ? "url(#ovGold)" : "rgba(255,255,255,0.06)"} />
              <title>{`${d.date}: ${fmtMoney(d.amount, cur)}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[9px] text-neutral-600 mt-1">
        <span>{data[0]?.date}</span><span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
};

/* ---------- main ---------- */
export const AdminOverviewSection = ({ onNavigate }) => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reflectedShare, setReflectedShare] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  const markReflected = async (u) => {
    setMarkingId(u.installment_id);
    try {
      const r = await axios.post(`${API}/admin/payment-plans/${u.plan_id}/installments/${u.installment_id}/reflected`,
        { reflected: true, notify: true }, { headers: authHeader() });
      toast.success("Abono marcado como reflejado — cliente avisado");
      if (r.data.message) {
        setReflectedShare({ message: r.data.message, client_name: r.data.client_name, client_phone: r.data.client_phone, amount: u.amount, currency: u.currency, service_name: r.data.service_name });
      }
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar");
    } finally { setMarkingId(null); }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/overview`, { headers: authHeader() });
      setData(r.data);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useRealtimeEvents({ admin_refresh: () => load(true), notification: () => load(true), message: () => load(true) });

  const go = (tab, extra) => onNavigate?.(tab, extra);
  const pendingByCur = useMemo(() => Object.entries(data?.payments?.pending_by_currency || {}), [data]);

  if (loading || !data) {
    return <div className="text-center py-12"><Loader2 className="w-8 h-8 text-[#d4af37] animate-spin mx-auto" /></div>;
  }
  const { alerts, payments, users, appointments, vidente, promotions } = data;

  const quick = [
    { tab: "users", label: "Usuarios", icon: Users, sub: `${users.total} · ${users.online} en línea` },
    { tab: "plans", label: "Pagos / Trabajos", icon: Wallet, sub: `${payments.plans_active} activos` },
    { tab: "promotions", label: "Promociones", icon: Gift, sub: `${promotions.active} activas` },
    { tab: "payment-requests", label: "Solicitudes pago", icon: Receipt, sub: `${alerts.payment_requests_pending} pendientes` },
    { tab: "appts", label: "Citas", icon: Calendar, sub: `${appointments.today} hoy` },
    { tab: "readings", label: "Lecturas", icon: Sparkle, sub: `${alerts.readings_in_progress} en curso` },
    { tab: "messages", label: "Mensajes", icon: MessageSquare, sub: `${data.messages.today} hoy` },
    { tab: "reports", label: "Administración", icon: BarChart3, sub: "KPIs y reportes" },
    { tab: "vidente-monitor", label: "Monitor Vidente", icon: Bot, sub: `${vidente.active_grants} accesos` },
    { tab: "vidente-ratings", label: "Reseñas", icon: Star, sub: `${vidente.rating_avg || "—"} ★` },
    { tab: "sorteo", label: "Sorteo", icon: Trophy, sub: `${data.sorteo.active} activo(s)` },
    { tab: "broadcast", label: "Broadcast", icon: Megaphone, sub: "Mensaje masivo" },
    { tab: "settings", label: "Configuración", icon: Settings, sub: "Precios y lecturas" },
    { tab: "activity", label: "Actividad", icon: Activity, sub: "Bitácora" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-overview-section">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-white flex items-center gap-2">
            Centro de mando
            {alerts.total > 0 && <span data-testid="overview-alert-total" className="text-[10px] font-sans font-bold bg-rose-500 text-white rounded-full px-2 py-0.5 animate-pulse">{alerts.total} alertas</span>}
          </h2>
          <p className="text-xs text-neutral-400">Actualizado {fmtDT(data.generated_at)} · se refresca en tiempo real</p>
        </div>
        <button onClick={() => load()} data-testid="overview-refresh" className="text-xs px-3 py-1.5 rounded-full bg-black/40 border border-white/10 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Refrescar
        </button>
      </div>

      {/* Alert chips */}
      <div className="flex flex-wrap gap-2" data-testid="overview-alert-chips">
        <AlertChip icon={ShieldAlert} tone="rose" label="Promesas vencidas" count={alerts.overdue_promises.length} onClick={() => go("plans")} testid="alert-overdue" />
        <AlertChip icon={Clock} tone="amber" label="Promos por vencer (48h)" count={alerts.promos_expiring_48h.length} onClick={() => go("promotions")} testid="alert-promos" />
        <AlertChip icon={Receipt} tone="sky" label="Solicitudes de pago" count={alerts.payment_requests_pending} onClick={() => go("payment-requests")} testid="alert-payreq" />
        <AlertChip icon={ClipboardEdit} tone="purple" label="Cambios de perfil" count={alerts.profile_requests_pending} onClick={() => go("profile-reqs")} testid="alert-profile" />
        <AlertChip icon={MessageCircle} tone="emerald" label="Chats sin responder" count={alerts.unanswered_chats.length} onClick={() => go("messages")} testid="alert-chats" />
        <AlertChip icon={Receipt} tone="amber" label="Abonos sin reflejar" count={(alerts.unreflected_installments || []).length} onClick={() => go("plans")} testid="alert-unreflected" />
        <AlertChip icon={Sparkle} tone="purple" label="Lecturas por iniciar" count={alerts.readings_granted_waiting} onClick={() => go("readings")} testid="alert-readings" />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={TrendingUp} tone="emerald" label="Recaudado 30d" value={fmtMoney(payments.revenue_this_month)} sub={`${payments.installments_count} abonos históricos`} testid="ov-revenue-30d" onClick={() => go("reports")} />
        <Kpi icon={Wallet} tone="gold" label="Recaudado total" value={fmtMoney(payments.revenue_total)} sub={`${payments.plans_fully_paid}/${payments.plans_total} trabajos liquidados`} testid="ov-revenue-total" onClick={() => go("plans")} />
        <Kpi icon={AlertTriangle} tone="amber" label="Cartera pendiente" value={pendingByCur.length ? fmtMoney(pendingByCur[0][1], pendingByCur[0][0]) : "$0"} sub={pendingByCur.slice(1).map(([c, v]) => fmtMoney(v, c)).join(" · ") || `${payments.plans_active} planes activos`} testid="ov-pending" onClick={() => go("plans")} />
        <Kpi icon={Users} tone="sky" label="Usuarios" value={users.total} sub={`${users.online} en línea · +${users.new_this_week} esta semana`} testid="ov-users-total" onClick={() => go("users")} />
        <Kpi icon={Calendar} tone="purple" label="Citas hoy" value={appointments.today} sub={`${appointments.this_week} en 7 días · ${appointments.pending} pendientes`} testid="ov-apt-today" onClick={() => go("appts")} />
        <Kpi icon={Gift} tone="rose" label="Promos" value={`${promotions.active} activas`} sub={`${promotions.accepted} liquidadas · ${fmtMoney(promotions.discount_accepted_total)} descontado`} testid="ov-promos" onClick={() => go("promotions")} />
      </div>

      {/* Chart + alert lists */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Panel title="Ingresos · últimos 30 días" icon={TrendingUp} className="xl:col-span-2" testid="overview-chart-panel">
          <RevenueChart series={data.revenue_30d} />
        </Panel>

        <Panel title="Promesas de pago vencidas" icon={ShieldAlert} testid="overview-overdue-panel"
          action={<button type="button" onClick={() => go("plans")} className="text-[10px] text-[#d4af37] flex items-center gap-1">Ver todo <ArrowRight className="w-3 h-3" /></button>}>
          {alerts.overdue_promises.length === 0 ? (
            <div className="text-xs text-neutral-500 italic flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Sin cartera vencida. ¡Todo al corriente!</div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {alerts.overdue_promises.map((o) => (
                <div key={o.plan_id} className="flex items-center gap-2 text-xs bg-rose-500/[0.07] border border-rose-500/20 rounded-lg px-2.5 py-1.5" data-testid={`overdue-${o.plan_id}`}>
                  <div className="flex-1 min-w-0">
                    <button type="button" onClick={() => openDossier(o.user_id)} className="text-white font-semibold truncate hover:text-[#d4af37] block max-w-full text-left">{o.user_name || o.user_visible_id}</button>
                    <div className="text-[10px] text-neutral-500 truncate">{o.service_name} · debe <b className="text-rose-300">{fmtMoney(o.remaining, o.currency)}</b></div>
                  </div>
                  <span className="text-[10px] text-rose-300 whitespace-nowrap">{o.days_overdue} d</span>
                  <button type="button" onClick={() => openChat(o.user_id, o.user_name)} className="p-1.5 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" title="Abrir chat"><MessageCircle className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Promos expiring + unanswered chats + upcoming */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel title="Promociones por vencer" icon={Clock} testid="overview-promos-panel"
          action={<button type="button" onClick={() => go("promotions")} className="text-[10px] text-[#d4af37] flex items-center gap-1">Prorrogar <ArrowRight className="w-3 h-3" /></button>}>
          {alerts.promos_expiring_48h.length === 0 ? <div className="text-xs text-neutral-500 italic">Ninguna promo vence en las próximas 48 h.</div> : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {alerts.promos_expiring_48h.map((p) => (
                <div key={p.promo_id} className="text-xs bg-amber-500/[0.07] border border-amber-500/25 rounded-lg px-2.5 py-1.5" data-testid={`promo-exp-${p.promo_id}`}>
                  <div className="flex justify-between gap-2"><span className="text-white font-semibold truncate">{p.user_name}</span><span className="text-amber-300 whitespace-nowrap">{fmtDT(p.valid_until)}</span></div>
                  <div className="text-[10px] text-neutral-500 truncate">{p.service_name} · paga {fmtMoney(p.new_remaining, p.currency)}{p.extended_days_total ? ` · +${p.extended_days_total}d` : ""}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Chats sin responder" icon={MessageCircle} testid="overview-chats-panel">
          {alerts.unanswered_chats.length === 0 ? <div className="text-xs text-neutral-500 italic flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Todos los chats atendidos.</div> : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {alerts.unanswered_chats.map((c) => (
                <button key={c.user_id} type="button" onClick={() => openChat(c.user_id, c.user_name)} data-testid={`unanswered-${c.user_id}`} className="w-full text-left text-xs bg-black/40 border border-white/5 hover:border-emerald-500/40 rounded-lg px-2.5 py-1.5">
                  <div className="flex justify-between gap-2"><span className="text-white font-semibold truncate">{c.user_name}</span><span className="text-[10px] text-neutral-500 whitespace-nowrap">{ago(c.at)}</span></div>
                  <div className="text-[10px] text-neutral-400 truncate">{c.preview || "…"}</div>
                </button>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Próximos cobros (7 días)" icon={Calendar} testid="overview-upcoming-panel">
          {alerts.upcoming_promises_7d.length === 0 ? <div className="text-xs text-neutral-500 italic">Sin promesas de pago en los próximos 7 días.</div> : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {alerts.upcoming_promises_7d.map((u) => (
                <div key={u.plan_id} className="text-xs bg-black/40 border border-white/5 rounded-lg px-2.5 py-1.5 flex justify-between gap-2" data-testid={`upcoming-${u.plan_id}`}>
                  <div className="min-w-0"><div className="text-white font-semibold truncate">{u.user_name}</div><div className="text-[10px] text-neutral-500 truncate">{u.service_name}</div></div>
                  <div className="text-right whitespace-nowrap"><div className="text-[#d4af37]">{fmtMoney(u.remaining, u.currency)}</div><div className="text-[10px] text-neutral-500">{u.promise_date}</div></div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Unreflected installments — quick action */}
      {(alerts.unreflected_installments || []).length > 0 && (
        <Panel title="Abonos pendientes de reflejarse" icon={Receipt} testid="overview-unreflected-panel">
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {alerts.unreflected_installments.map((u) => (
              <div key={u.installment_id} className="flex items-center gap-2 text-xs bg-amber-500/[0.07] border border-amber-500/25 rounded-lg px-2.5 py-1.5" data-testid={`unreflected-${u.installment_id}`}>
                <div className="flex-1 min-w-0">
                  <button type="button" onClick={() => openDossier(u.user_id)} className="text-white font-semibold truncate hover:text-[#d4af37] block max-w-full text-left">{u.user_name}</button>
                  <div className="text-[10px] text-neutral-500 truncate">{u.service_name} · <b className="text-amber-200">{fmtMoney(u.amount, u.currency)}</b>{u.method ? ` · ${u.method}` : ""} · {ago(u.at)}</div>
                </div>
                <button
                  type="button"
                  data-testid={`unreflected-mark-${u.installment_id}`}
                  disabled={markingId === u.installment_id}
                  onClick={() => markReflected(u)}
                  className="text-[10px] px-2.5 py-1 rounded-full border border-emerald-400/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 font-semibold whitespace-nowrap disabled:opacity-50"
                >
                  {markingId === u.installment_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "✓ Ya se reflejó"}
                </button>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Quick access */}
      <Panel title="Accesos rápidos" icon={ArrowRight} testid="overview-quick-access">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {quick.map((q) => (
            <button key={q.tab} type="button" onClick={() => go(q.tab)} data-testid={`quick-${q.tab}`} className="group text-left rounded-xl border border-white/10 bg-black/30 hover:border-[#d4af37]/50 hover:bg-[#d4af37]/[0.06] px-3 py-2.5 transition">
              <q.icon className="w-4 h-4 text-[#d4af37] mb-1.5 group-hover:scale-110 transition-transform" />
              <div className="text-[11px] text-white font-semibold leading-tight">{q.label}</div>
              <div className="text-[9px] text-neutral-500 truncate">{q.sub}</div>
            </button>
          ))}
        </div>
      </Panel>

      {/* Feeds */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Panel title="Últimos abonos" icon={Receipt} testid="overview-ledger">
          <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
            {data.recent_installments.length === 0 && <div className="text-xs text-neutral-500 italic">Sin abonos aún.</div>}
            {data.recent_installments.map((i, idx) => (
              <div key={`${i.plan_id}-${i.at}-${idx}`} className="text-xs flex justify-between gap-2 border-b border-white/5 pb-1.5">
                <div className="min-w-0"><div className="text-white truncate">{i.user_name}</div><div className="text-[10px] text-neutral-500 truncate">{i.service_name}{i.method ? ` · ${i.method}` : ""}</div></div>
                <div className="text-right whitespace-nowrap"><div className="text-emerald-300 font-semibold">{fmtMoney(i.amount, i.currency)}</div><div className="text-[10px] text-neutral-600">{ago(i.at)}</div></div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Citas de hoy" icon={Calendar} testid="overview-today-apts">
          <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
            {appointments.today_list.length === 0 && <div className="text-xs text-neutral-500 italic">No hay citas programadas para hoy.</div>}
            {appointments.today_list.map((a) => (
              <div key={a.id} className="text-xs flex justify-between gap-2 border-b border-white/5 pb-1.5">
                <div className="min-w-0"><div className="text-white truncate">{a.user_name || a.user_visible_id}</div><div className="text-[10px] text-neutral-500 truncate">{a.service_name}</div></div>
                <div className="text-right whitespace-nowrap"><div className="text-[#d4af37] font-mono">{a.scheduled_time}</div><div className="text-[10px] text-neutral-500">{a.status}</div></div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Últimos usuarios" icon={IdCard} testid="overview-latest-users">
          <div className="space-y-1.5">
            {data.latest_users.map((u) => (
              <button key={u.id} type="button" onClick={() => openDossier(u.id)} className="w-full text-xs text-neutral-300 flex justify-between items-center border-b border-white/5 pb-1.5 hover:text-[#d4af37]">
                <span className="flex items-center gap-1.5 truncate"><span className={`w-1.5 h-1.5 rounded-full ${u.online ? "bg-emerald-400" : "bg-neutral-700"}`} />{u.name || u.username || u.id?.slice(0, 8)}</span>
                <span className="text-neutral-500 whitespace-nowrap">{u.created_at ? fmtDT(u.created_at, { day: "2-digit", month: "short" }) : "—"}</span>
              </button>
            ))}
          </div>
        </Panel>

        <Panel title="Actividad reciente" icon={Activity} testid="overview-activity">
          <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
            {data.recent_activity.length === 0 && <div className="text-xs text-neutral-500 italic">Sin actividad.</div>}
            {data.recent_activity.map((a, i) => (
              <div key={i} className="text-[11px] text-neutral-300 border-b border-white/5 pb-1.5">
                <div className="truncate">{a.detail || a.type}</div>
                <div className="text-[10px] text-neutral-600">{ago(a.at)}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Platform state */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={Bot} tone="purple" label="Vidente Virtual" value={vidente.active_grants} sub={`${vidente.sessions_this_week} sesiones/7d · ${vidente.rating_avg || "—"} ★ (${vidente.rating_count})`} testid="ov-vid-grants" onClick={() => go("vidente-monitor")} />
        <Kpi icon={Moon} tone="sky" label="Horóscopo activo" value={data.horoscope.active_grants} testid="ov-horoscope" />
        <Kpi icon={Trophy} tone="gold" label="Sorteos" value={data.sorteo.total} sub={`${data.sorteo.active} activos · ${data.sorteo.completed} completos`} testid="ov-sorteo" onClick={() => go("sorteo")} />
        <Kpi icon={MessageSquare} tone="emerald" label="Mensajes hoy" value={data.messages.today} sub={`${data.messages.this_week} esta semana`} testid="ov-messages" onClick={() => go("messages")} />
        <Kpi icon={Gamepad2} tone="rose" label="Juegos hoy" value={data.games.daily_card_today + data.games.oracle_today} sub={`${data.games.memory_total} partidas memoria`} testid="ov-games" />
        <Kpi icon={BellRing} tone="amber" label="Solicitudes" value={alerts.payment_requests_pending + alerts.profile_requests_pending} sub={`${payments.requests.approved} ✔ · ${payments.requests.rejected} ✖`} testid="ov-requests" onClick={() => go("payment-requests")} />
      </div>

      <ReflectedShareModal data={reflectedShare} onClose={() => setReflectedShare(null)} />
    </div>
  );
};
